import React, { useState } from "react";

import { PartnerPayoutDto } from "services/partners/restModels";
import { formatDate } from "shared/lib/dateTime";
import { formatPrice } from "shared/lib/price";
import { DataTable, DataTableColumn } from "shared/ui/DataTable";
import { IconButton } from "shared/ui/IconButton";
import { DeleteIcon } from "shared/ui/icons";
import { Pagination } from "shared/ui/Pagination";

import { PAGE_SIZE } from "../constants";

interface PayoutsTableProps {
    items: PartnerPayoutDto[];
    canManage: boolean;
    showPartner: boolean;
    onDelete: (payout: PartnerPayoutDto) => void;
}

export const PayoutsTable = ({ items, canManage, showPartner, onDelete }: PayoutsTableProps) => {
    const [page, setPage] = useState(1);

    // Выплаты ограничены периодом и приходят целиком. Страница режется здесь, а после удаления
    // последней записи на странице возвращаемся на предыдущую.
    const lastPage = Math.max(Math.ceil(items.length / PAGE_SIZE), 1);
    const currentPage = Math.min(page, lastPage);
    const rows = items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const columns: DataTableColumn<PartnerPayoutDto>[] = [
        {
            key: "amount",
            title: "Сумма",
            role: "primary",
            width: 180,
            render: (payout) => formatPrice(payout.amount, payout.currencyId),
        },
        {
            key: "paidAt",
            title: "Дата",
            role: "secondary",
            width: 110,
            render: (payout) => formatDate(payout.paidAt),
        },
        {
            key: "comment",
            title: "Комментарий",
            role: "secondary",
            grow: true,
            render: (payout) => payout.comment || "—",
        },
    ];

    if (showPartner) {
        columns.splice(2, 0, {
            key: "partner",
            title: "Партнёр",
            role: "secondary",
            width: 180,
            render: (payout) => payout.partnerName || "—",
        });
    }

    if (canManage) {
        columns.push({
            key: "actions",
            role: "action",
            width: 56,
            render: (payout) => (
                <IconButton label="Удалить выплату" onClick={() => onDelete(payout)}>
                    <DeleteIcon />
                </IconButton>
            ),
        });
    }

    return (
        <DataTable
            columns={columns}
            rows={rows}
            rowKey={(payout) => payout.id}
            footer={
                <Pagination
                    page={currentPage}
                    pageSize={PAGE_SIZE}
                    total={items.length}
                    onChange={setPage}
                />
            }
        />
    );
};
